import React, { useEffect, useRef, useState } from "react";
import { Toaster, toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import { TempWaNotice } from "../components/TempWaNotice";
import { Mail, Phone, MapPin, Instagram, Facebook, ArrowRight, ArrowLeft, ExternalLink } from "lucide-react";
import { submitInquiry } from "../lib/api";
import { COMPANY, LOCATIONS } from "../content";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import { track } from "../lib/analytics";
import { getUtms, attributionSummary } from "../lib/waTracker";

// Customer-facing service types → backend service_tag (only fitout/renovation/
// curtains are valid tags; anything else is submitted with no tag).
const SERVICE_TYPES = [
  { label: "Full Interior Fit-Out", tag: "fitout" },
  { label: "Renovation / Remodel", tag: "renovation" },
  { label: "Office / Commercial Fit-Out", tag: "fitout" },
  { label: "Custom Joinery & Furniture", tag: null },
  { label: "Curtains & Blinds", tag: "curtains" },
  { label: "Not sure yet", tag: null },
];

const EMIRATES = ["Dubai", "Abu Dhabi", "Sharjah", "Ajman", "Ras Al Khaimah", "Fujairah", "Umm Al Quwain", "Other"];
const PROPERTY_TYPES = ["Villa", "Apartment", "Office", "Retail / F&B", "Hospitality", "Other"];
const BUDGETS = ["Under AED 50,000", "AED 50,000 – 150,000", "AED 150,000 – 500,000", "AED 500,000+", "Not sure yet"];
const TIMEFRAMES = ["As soon as possible", "Within 1–3 months", "In 3–6 months", "Just exploring"];

const PHONE_RE = /^[+()\-\s\d]{7,20}$/;

const initial = {
  service: "",
  service_tag: null,
  emirate: "",
  phone: "",
  name: "",
  email: "",
  property_type: "",
  budget: "",
  timeframe: "",
  details: "",
};

export default function Contact() {
  usePageMeta({
    title: "Contact Time Furniture | Interior Fit-Out Contractor Dubai & Sharjah",
    description:
      "Talk to Time Furniture — turnkey fit-out, renovation, joinery and curtains across the UAE. Book a site visit or request a quote. Established 1993.",
    canonical: "https://timefurniture.ae/contact",
    ogImage: "/projects/address-downtown-dark-1.webp",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      url: "https://timefurniture.ae/contact",
      mainEntity: { "@id": "https://timefurniture.ae/#organization" },
    },
  });

  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const startedRef = useRef(false);
  const step2Ref = useRef(null);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
    if (!startedRef.current) {
      startedRef.current = true;
      track.formStart({ form: "contact" });
    }
  };

  useEffect(() => {
    if (step === 2 && step2Ref.current) {
      const el = step2Ref.current.querySelector("input,select,textarea,button");
      if (el) el.focus();
    }
  }, [step]);

  const validateStep1 = () => {
    const e = {};
    if (!form.service) e.service = "Please choose a service type.";
    if (!form.emirate) e.emirate = "Please select your emirate or area.";
    if (!form.phone.trim()) e.phone = "Mobile / WhatsApp number is required.";
    else if (!PHONE_RE.test(form.phone.trim()) || form.phone.replace(/\D/g, "").length < 7 || form.phone.replace(/\D/g, "").length > 15)
      e.phone = "Please enter a valid phone number.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = "Please enter a valid email or leave it blank.";
    if (!form.details.trim()) e.details = "Please tell us a little about your project.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const selectService = (label, tag) => {
    setForm((f) => ({ ...f, service: label, service_tag: tag }));
    setErrors((e) => ({ ...e, service: undefined }));
    if (!startedRef.current) {
      startedRef.current = true;
      track.formStart({ form: "contact" });
    }
  };

  const goToStep2 = () => {
    if (!validateStep1()) return;
    track.formStepComplete(1, { form: "contact", service: form.service, emirate: form.emirate });
    setStep(2);
  };

  const buildMessage = () => {
    const utm = getUtms();
    const lines = [
      form.details.trim(),
      "",
      "— Enquiry details —",
      `Service: ${form.service}`,
      `Emirate / area: ${form.emirate}`,
      form.property_type ? `Property type: ${form.property_type}` : null,
      form.budget ? `Budget: ${form.budget}` : null,
      form.timeframe ? `Preferred start: ${form.timeframe}` : null,
      form.email.trim() ? null : "(No email provided — contact by phone/WhatsApp)",
    ];
    const utmKeys = Object.keys(utm || {});
    if (utmKeys.length) {
      lines.push("");
      lines.push(`Source: ${utmKeys.map((k) => `${k}=${utm[k]}`).join(", ")}`);
    }
    const attr = attributionSummary("contact-form");
    if (attr) {
      lines.push("");
      lines.push(attr);
    }
    return lines.filter((l) => l !== null).join("\n");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;
    setLoading(true);
    const digits = form.phone.replace(/\D/g, "");
    const email = form.email.trim() || `${digits || "web"}@web-lead.timefurniture.ae`;
    const payload = {
      name: form.name.trim(),
      email,
      phone: form.phone.trim(),
      service: form.service,
      service_tag: form.service_tag || undefined,
      location: form.emirate,
      message: buildMessage(),
      source_page: "/contact",
    };
    try {
      await submitInquiry(payload);
      track.formSubmitSuccess({ form: "contact", service: form.service, emirate: form.emirate });
      setForm(initial);
      navigate("/thank-you");
    } catch (err) {
      track.formSubmitError({ form: "contact" });
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#FAF5EC]">
      <Toaster theme="light" position="top-center" />
      <Breadcrumbs testid="contact-breadcrumbs" items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-10">
        <p className="eyebrow mb-6">Contact</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight max-w-4xl">
          Book a <em className="text-[#6E1F2C] not-italic">site visit</em>.
        </h1>
        <p className="mt-6 max-w-2xl text-[#7A6B5F] leading-relaxed">
          Two quick steps. Tell us what you need and how to reach you — a member of our team responds within one business day.
        </p>
        <p className="mt-4 text-sm text-[#7A6B5F]">
          Prefer a ballpark first?{" "}
          <Link to="/fit-out-cost-calculator-dubai" data-testid="contact-planner-link" className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]">
            Use the Fit-Out Budget Planner
          </Link>.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* FORM */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            noValidate
            className="lg:col-span-7 border border-[#6E1F2C]/15 p-6 sm:p-8 md:p-10 bg-white"
          >
            {/* Step indicator */}
            <div className="flex items-center gap-3 mb-8" data-testid="contact-step-indicator">
              {[1, 2].map((s) => (
                <div key={s} className="flex items-center gap-3">
                  <span
                    className={`w-7 h-7 inline-flex items-center justify-center text-[11px] rounded-full border ${
                      step >= s ? "bg-[#6E1F2C] text-[#FAF5EC] border-[#6E1F2C]" : "text-[#7A6B5F] border-[#6E1F2C]/25"
                    }`}
                  >
                    {s}
                  </span>
                  <span className={`text-[10px] tracking-[0.24em] uppercase ${step >= s ? "text-[#1C110C]" : "text-[#7A6B5F]"}`}>
                    {s === 1 ? "Your need" : "Your details"}
                  </span>
                  {s === 1 && <span className="w-8 h-px bg-[#6E1F2C]/20" aria-hidden="true" />}
                </div>
              ))}
            </div>

            {/* STEP 1 */}
            {step === 1 && (
              <div data-testid="contact-step-1" className="space-y-7">
                <fieldset>
                  <legend className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-3">
                    What do you need? *
                  </legend>
                  <div role="radiogroup" aria-label="Service type" className="flex flex-wrap gap-2.5">
                    {SERVICE_TYPES.map(({ label, tag }) => {
                      const selected = form.service === label;
                      return (
                        <button
                          key={label}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          data-testid={`service-pill-${label.toLowerCase().replace(/[^\w]+/g, "-")}`}
                          onClick={() => selectService(label, tag)}
                          className={`px-4 py-2.5 rounded-full text-xs tracking-[0.1em] border transition-all duration-200 ${
                            selected
                              ? "bg-[#D4A963] text-[#1C110C] border-[#D4A963] shadow-[0_2px_12px_rgba(212,169,99,0.35)]"
                              : "bg-transparent text-[#1C110C] border-[#6E1F2C]/25 hover:border-[#D4A963] hover:text-[#6E1F2C]"
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  {errors.service && <p data-testid="error-service" className="mt-2 text-xs text-[#A3141A]">{errors.service}</p>}
                </fieldset>

                <div>
                  <label htmlFor="emirate" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-2">
                    Emirate / area *
                  </label>
                  <select
                    id="emirate"
                    name="emirate"
                    required
                    data-testid="input-emirate"
                    value={form.emirate}
                    onChange={(e) => update("emirate", e.target.value)}
                    aria-invalid={!!errors.emirate}
                    className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-[#1C110C] text-sm focus-gold"
                  >
                    <option value="">Select…</option>
                    {EMIRATES.map((e) => <option key={e} value={e}>{e}</option>)}
                  </select>
                  {errors.emirate && <p data-testid="error-emirate" className="mt-2 text-xs text-[#A3141A]">{errors.emirate}</p>}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-2">
                    Mobile / WhatsApp number *
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    required
                    placeholder="+971 5X XXX XXXX"
                    data-testid="input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    aria-invalid={!!errors.phone}
                    aria-describedby={errors.phone ? "phone-error" : undefined}
                    className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-[#1C110C] text-sm focus-gold"
                  />
                  {errors.phone && <p id="phone-error" data-testid="error-phone" className="mt-2 text-xs text-[#A3141A]">{errors.phone}</p>}
                </div>

                <button
                  type="button"
                  onClick={goToStep2}
                  data-testid="contact-next-btn"
                  className="inline-flex items-center gap-3 bg-[#6E1F2C] text-[#FAF5EC] px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors"
                >
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <div ref={step2Ref} data-testid="contact-step-2" className="space-y-7">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-2">Full name *</label>
                    <input
                      id="name" name="name" type="text" autoComplete="name" required
                      data-testid="input-name" value={form.name}
                      onChange={(e) => update("name", e.target.value)} aria-invalid={!!errors.name}
                      className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-[#1C110C] text-sm focus-gold"
                    />
                    {errors.name && <p data-testid="error-name" className="mt-2 text-xs text-[#A3141A]">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-2">Email <span className="normal-case tracking-normal text-[#A89A8C]">(optional)</span></label>
                    <input
                      id="email" name="email" type="email" autoComplete="email"
                      data-testid="input-email" value={form.email}
                      onChange={(e) => update("email", e.target.value)} aria-invalid={!!errors.email}
                      className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-[#1C110C] text-sm focus-gold"
                    />
                    {errors.email && <p data-testid="error-email" className="mt-2 text-xs text-[#A3141A]">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="property_type" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-2">Property / project type</label>
                    <select id="property_type" name="property_type" data-testid="input-property-type" value={form.property_type}
                      onChange={(e) => update("property_type", e.target.value)}
                      className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-[#1C110C] text-sm focus-gold">
                      <option value="">Select…</option>
                      {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="budget" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-2">Budget range</label>
                    <select id="budget" name="budget" data-testid="input-budget" value={form.budget}
                      onChange={(e) => update("budget", e.target.value)}
                      className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-[#1C110C] text-sm focus-gold">
                      <option value="">Select…</option>
                      {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="timeframe" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-2">Preferred start</label>
                  <select id="timeframe" name="timeframe" data-testid="input-timeframe" value={form.timeframe}
                    onChange={(e) => update("timeframe", e.target.value)}
                    className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-[#1C110C] text-sm focus-gold">
                    <option value="">Select…</option>
                    {TIMEFRAMES.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
                  </select>
                </div>

                <div>
                  <label htmlFor="details" className="block text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] mb-2">Tell us about your project *</label>
                  <textarea
                    id="details" name="details" rows={5} required
                    data-testid="input-message" value={form.details}
                    onChange={(e) => update("details", e.target.value)} aria-invalid={!!errors.details}
                    placeholder="Scope, rooms, timeline, style references…"
                    className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/25 px-4 py-3 text-[#1C110C] text-sm focus-gold resize-none"
                  />
                  {errors.details && <p data-testid="error-details" className="mt-2 text-xs text-[#A3141A]">{errors.details}</p>}
                </div>

                <p data-testid="contact-consent" className="text-xs text-[#7A6B5F] leading-relaxed">
                  By submitting, you agree that Time Furniture may contact you about this enquiry. We use your details only to respond to and manage your request.
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button" onClick={() => setStep(1)} data-testid="contact-back-btn"
                    className="inline-flex items-center gap-2 border border-[#6E1F2C]/30 text-[#6E1F2C] px-6 py-4 text-[11px] tracking-[0.28em] uppercase hover:border-[#6E1F2C] transition-colors"
                  >
                    <ArrowLeft size={15} /> Back
                  </button>
                  <button
                    type="submit" disabled={loading} data-testid="submit-inquiry-btn"
                    className="inline-flex items-center gap-3 bg-[#6E1F2C] text-[#FAF5EC] px-10 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors disabled:opacity-60"
                  >
                    {loading ? "Sending…" : "Book a Site Visit"}
                  </button>
                </div>
              </div>
            )}
          </form>

          {/* INFO */}
          <aside className="lg:col-span-5 space-y-8">
            <div>
              <p className="eyebrow mb-5">Direct</p>
              <ul className="space-y-5">
                <li className="flex gap-4">
                  <Phone className="text-[#6E1F2C] mt-1" size={16} />
                  <div>
                    <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} data-testid="contact-phone"
                      onClick={() => track.phoneClick("contact_aside")} className="text-[#1C110C] link-elegant">
                      {COMPANY.phone}
                    </a>
                    <p className="text-xs text-[#7A6B5F] mt-1">Mon – Sat · 9am – 7pm</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <Mail className="text-[#6E1F2C] mt-1" size={16} />
                  <div>
                    <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="text-[#1C110C] link-elegant">{COMPANY.email}</a>
                    <p className="text-xs text-[#7A6B5F] mt-1">General & sales</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <MapPin className="text-[#6E1F2C] mt-1" size={16} />
                  <div>
                    <p className="text-[#1C110C]">Dubai · Sharjah · Al Sajja</p>
                    <p className="text-xs text-[#7A6B5F] mt-1">UAE · India</p>
                  </div>
                </li>
              </ul>
              <div className="mt-6 flex gap-3">
                <a href={`https://instagram.com/${COMPANY.instagram}`} target="_blank" rel="noreferrer"
                  className="w-10 h-10 border border-[#6E1F2C]/25 inline-flex items-center justify-center hover:border-[#6E1F2C] hover:text-[#6E1F2C]" aria-label="Instagram">
                  <Instagram size={16} />
                </a>
                <a href={`https://facebook.com/${COMPANY.facebook}`} target="_blank" rel="noreferrer"
                  className="w-10 h-10 border border-[#6E1F2C]/25 inline-flex items-center justify-center hover:border-[#6E1F2C] hover:text-[#6E1F2C]" aria-label="Facebook">
                  <Facebook size={16} />
                </a>
              </div>
            </div>

            <div className="bg-white border border-[#6E1F2C]/15 p-6">
              <p className="eyebrow mb-4">Quick Chat</p>
              <p className="text-sm text-[#7A6B5F] leading-relaxed mb-4">Prefer a quick message? Reach our team instantly on WhatsApp.</p>
              <TempWaNotice className="mb-4" />
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappContactText}`} target="_blank" rel="noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-flex items-center gap-3 bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors">
                Open WhatsApp
              </a>
            </div>

            {/* Compact studio cards (lighter than embedded maps) */}
            <div>
              <p className="eyebrow mb-4">Studios & Factory</p>
              <ul className="space-y-3">
                {LOCATIONS.map((l) => (
                  <li key={l.name} className="bg-white border border-[#6E1F2C]/15 p-4"
                    data-testid={`location-${l.city.toLowerCase().replace(/\s+/g, "-")}`}>
                    <p className="text-[10px] tracking-[0.28em] uppercase text-[#6E1F2C]">{l.city}</p>
                    <p className="font-serif-display text-base mt-1 text-[#1C110C]">{l.name}</p>
                    <p className="mt-1 text-xs text-[#7A6B5F] leading-relaxed">{l.address}</p>
                    <a href={`https://maps.google.com/maps?q=${encodeURIComponent(l.mapQuery)}`} target="_blank" rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-[#6E1F2C] hover:text-[#56151F]">
                      View on Google Maps <ExternalLink size={11} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
